import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import Markup from '../../../../atoms/Markup'
import {
  labelStyle, valueStyle as baseValueStyle, describeRecurrence
} from '../shared/redtailUIHelpers'
import { LinkedContactDetail } from '../shared/LinkedContactDisplay'
import { toLocal, LOCAL_TZ_ABBR, toDatetimeLocalValue } from './meetingHelpers'
import MeetingForm from './MeetingForm'
import useCategoryOptions from './useCategoryOptions'

const MODE_VIEWING = 'viewing'
const MODE_EDITING = 'editing'
const MODE_CONFIRMING = 'confirming'
const MODE_EDIT_SCOPE = 'edit-scope'
const MODE_DELETE_SCOPE = 'delete-scope'

function isRecurring (activity) {
  return (activity?.repeats && activity.repeats !== 'never') || !!activity?.recurrence_period
}

// TODO: Redtail uses the start_date unix timestamp as the instance identifier for
// recurring activities. Confirm this matches their API semantics across DST boundaries.
// Ideally the API response would include an explicit instance_id field.
function getInstanceId (activity) {
  if (!activity?.start_date) return null
  return dayjs(activity.start_date).unix()
}

const valueStyle = { ...baseValueStyle, marginBottom: '12px' }

function formatDetailDateTime (isoString, allDay) {
  if (!isoString) return null
  const local = toLocal(isoString)
  if (allDay) return local.format('ddd, MMM D, YYYY')
  const tzSuffix = LOCAL_TZ_ABBR ? ` ${LOCAL_TZ_ABBR}` : ''
  return `${local.format('ddd, MMM D, YYYY h:mma')}${tzSuffix}`
}

function buildContactOptions (activity) {
  if (!activity.linked_contacts?.length) return []
  return activity.linked_contacts.map(c => ({
    label: c.full_name || `Contact ${c.contact_id ?? c.id}`,
    value: c.contact_id ?? c.id,
    hoh: !!c.hoh
  }))
}

function computeDuration (activity) {
  if (!activity.start_date || !activity.end_date) return 60
  const diff = dayjs(activity.end_date).diff(dayjs(activity.start_date), 'minute')
  if (diff <= 0) return 60
  return diff
}

function buildFormDefaults (activity) {
  const allDay = activity.all_day || false
  return {
    subject: activity.subject || '',
    start_date: toDatetimeLocalValue(activity.start_date, allDay),
    end_date: toDatetimeLocalValue(activity.end_date, allDay),
    duration: allDay ? 60 : computeDuration(activity),
    all_day: allDay,
    contacts: buildContactOptions(activity),
    location: activity.location || '',
    description: activity.description || '',
    priority: activity.priority ?? 1,
    repeats: activity.repeats || 'never',
    category_id: activity.category_id ?? ''
  }
}

/* ── Read-only view ────────────────────────────────────────────────── */

function ViewingBody ({ activity, categoryOptions }) {
  const startFormatted = formatDetailDateTime(activity.start_date, activity.all_day)
  const endFormatted = formatDetailDateTime(activity.end_date, activity.all_day)

  const categoryName = useMemo(() => {
    if (!activity.category_id) return null
    const cat = categoryOptions.find(c => c.value === activity.category_id)
    return cat?.label ?? null
  }, [activity.category_id, categoryOptions])

  const repeatsLabel = useMemo(() => describeRecurrence(activity), [activity])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <LinkedContactDetail
        contacts={activity.linked_contacts}
        labelStyle={labelStyle}
        valueStyle={valueStyle}
      />

      <div style={{ display: 'flex', gap: '32px' }}>
        {startFormatted ? (
          <div>
            <div style={labelStyle}>Start</div>
            <div style={valueStyle}>{startFormatted}</div>
          </div>
        ) : null}
        {endFormatted ? (
          <div>
            <div style={labelStyle}>End</div>
            <div style={valueStyle}>{endFormatted}</div>
          </div>
        ) : null}
      </div>

      {activity.all_day ? (
        <div style={{ ...valueStyle, fontStyle: 'italic', color: 'var(--text-color-subtle)' }}>
          All-day event
        </div>
      ) : null}

      {activity.location ? (
        <>
          <div style={labelStyle}>Location</div>
          <div style={valueStyle}>{activity.location}</div>
        </>
      ) : null}

      {activity.description ? (
        <>
          <div style={labelStyle}>Description</div>
          <div style={valueStyle}>
            <Markup value={activity.description} type='html' style={{ fontSize: 'inherit' }} />
          </div>
        </>
      ) : null}

      {activity.priority ? (
        <>
          <div style={labelStyle}>Priority</div>
          <div style={valueStyle}>{activity.priority}</div>
        </>
      ) : null}

      {repeatsLabel ? (
        <>
          <div style={labelStyle}>Repeats</div>
          <div style={valueStyle}>{repeatsLabel}</div>
        </>
      ) : null}

      {categoryName ? (
        <>
          <div style={labelStyle}>Category</div>
          <div style={valueStyle}>{categoryName}</div>
        </>
      ) : null}
    </div>
  )
}

ViewingBody.propTypes = {
  activity: PropTypes.object.isRequired,
  categoryOptions: PropTypes.array
}

/* ── Delete confirmation ───────────────────────────────────────────── */

function ConfirmDeleteBody ({ activity, occurrenceScope, onDeleted, onCancel }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()

  const { mutate: handleDelete, isLoading: deleting, error } = useMutation({
    mutationFn: () => {
      if (occurrenceScope === 'instance') {
        const instanceId = getInstanceId(activity)
        return postNamedCommand('coreData', 'execute-redtail-domain-command', {
          command: 'removeActivityInstance',
          autoResolveConnection: true,
          clientId,
          request: { activityId: activity.id, instanceId }
        })
      }
      return postNamedCommand('coreData', 'execute-redtail-domain-command', {
        command: 'removeActivity',
        autoResolveConnection: true,
        clientId,
        request: { activityId: activity.id }
      })
    },
    onSuccess: () => {
      onDeleted()
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.redtailDomainQuery, 'list-preset']
      })
    }
  })

  const deleteError = error?.message || null
  const isInstance = occurrenceScope === 'instance'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ fontSize: 'var(--font-size-sm)', margin: 0 }}>
        {isInstance
          ? <>Are you sure you want to delete this occurrence of <strong>{activity.subject || 'this meeting'}</strong>?</>
          : <>Are you sure you want to delete <strong>{activity.subject || 'this meeting'}</strong>? This action cannot be undone.</>}
      </p>

      {deleteError ? (
        <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)' }}>
          {deleteError}
        </div>
      ) : null}

      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel} disabled={deleting}>
          Go Back
        </SydButton>
        <SydButton
          variant='primary'
          size='sm'
          processing={deleting}
          onClick={() => handleDelete()}
          style={{ backgroundColor: 'var(--color-decline-500)' }}
        >
          Delete Meeting
        </SydButton>
      </SydModalActions>
    </div>
  )
}

ConfirmDeleteBody.propTypes = {
  activity: PropTypes.object.isRequired,
  occurrenceScope: PropTypes.string,
  onDeleted: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

/* ── Recurrence scope choice ───────────────────────────────────────── */

function RecurrenceChoiceBody ({ action, onChooseAll, onChooseInstance, onCancel }) {
  const actionLabel = action === 'edit' ? 'edit' : 'delete'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ fontSize: 'var(--font-size-sm)', margin: 0 }}>
        This is a recurring meeting. Would you like to {actionLabel} this occurrence or all occurrences?
      </p>
      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel}>
          Cancel
        </SydButton>
        <SydButton variant='outline' size='sm' onClick={onChooseInstance}>
          This Occurrence
        </SydButton>
        <SydButton variant='primary' size='sm' onClick={onChooseAll}>
          All Occurrences
        </SydButton>
      </SydModalActions>
    </div>
  )
}

RecurrenceChoiceBody.propTypes = {
  action: PropTypes.oneOf(['edit', 'delete']).isRequired,
  onChooseAll: PropTypes.func.isRequired,
  onChooseInstance: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

/* ── Main modal ────────────────────────────────────────────────────── */

const TITLES = {
  [MODE_VIEWING]: null,
  [MODE_EDITING]: 'Edit Meeting',
  [MODE_CONFIRMING]: 'Delete Meeting',
  [MODE_EDIT_SCOPE]: 'Edit Meeting',
  [MODE_DELETE_SCOPE]: 'Delete Meeting'
}

function MeetingDetailModal ({ open, onClose, activity, config, members }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState(MODE_VIEWING)
  const [occurrenceScope, setOccurrenceScope] = useState('all')
  const [bladeOpen, setBladeOpen] = useState(false)

  const categoryOptions = useCategoryOptions(clientId, config?.categoryIds, open)

  useEffect(() => {
    if (open) {
      setMode(MODE_VIEWING)
      setOccurrenceScope('all')
      setBladeOpen(false)
    }
  }, [open])

  const recurring = activity && isRecurring(activity)

  const handleClose = useCallback(() => {
    setMode(MODE_VIEWING)
    setBladeOpen(false)
    onClose()
  }, [onClose])

  const handleEditClick = useCallback(() => {
    if (recurring) {
      setMode(MODE_EDIT_SCOPE)
    } else {
      setOccurrenceScope('all')
      setMode(MODE_EDITING)
    }
  }, [recurring])

  const handleDeleteClick = useCallback(() => {
    if (recurring) {
      setMode(MODE_DELETE_SCOPE)
    } else {
      setOccurrenceScope('all')
      setMode(MODE_CONFIRMING)
    }
  }, [recurring])

  const { mutateAsync: handleEditSubmit } = useMutation({
    mutationFn: (body) => {
      if (occurrenceScope === 'instance') {
        const instanceId = getInstanceId(activity)
        return postNamedCommand('coreData', 'execute-redtail-domain-command', {
          command: 'modifyActivityInstance',
          autoResolveConnection: true,
          clientId,
          request: { activityId: activity.id, instanceId, body }
        })
      }
      return postNamedCommand('coreData', 'execute-redtail-domain-command', {
        command: 'modifyActivity',
        autoResolveConnection: true,
        clientId,
        request: { activityId: activity.id, body }
      })
    },
    onSuccess: () => {
      handleClose()
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.redtailDomainQuery, 'list-preset']
      })
    }
  })

  if (!activity) return null

  const title = TITLES[mode] ?? (activity.subject || 'Meeting Details')

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      title={title}
      size={bladeOpen ? 'large' : 'xmedium'}
      disablePortal={false}
    >
      {mode === MODE_VIEWING && (
        <>
          <ViewingBody activity={activity} categoryOptions={categoryOptions} />
          <SydModalActions>
            <SydButton
              variant='outline'
              size='sm'
              onClick={handleDeleteClick}
              style={{ color: 'var(--color-decline-500)', borderColor: 'var(--color-decline-500)' }}
            >
              Delete
            </SydButton>
            <div style={{ flex: 1 }} />
            <SydButton variant='ghost' size='sm' onClick={handleClose}>
              Close
            </SydButton>
            <SydButton variant='outline' size='sm' icon='edit' onClick={handleEditClick}>
              Edit
            </SydButton>
          </SydModalActions>
        </>
      )}

      {mode === MODE_EDIT_SCOPE && (
        <RecurrenceChoiceBody
          action='edit'
          onChooseAll={() => { setOccurrenceScope('all'); setMode(MODE_EDITING) }}
          onChooseInstance={() => { setOccurrenceScope('instance'); setMode(MODE_EDITING) }}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}

      {mode === MODE_DELETE_SCOPE && (
        <RecurrenceChoiceBody
          action='delete'
          onChooseAll={() => { setOccurrenceScope('all'); setMode(MODE_CONFIRMING) }}
          onChooseInstance={() => { setOccurrenceScope('instance'); setMode(MODE_CONFIRMING) }}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}

      {mode === MODE_EDITING && (
        <MeetingForm
          defaultValues={buildFormDefaults(activity)}
          config={config}
          members={members}
          categoryOptions={categoryOptions}
          onSubmit={handleEditSubmit}
          onCancel={() => { setBladeOpen(false); setMode(MODE_VIEWING) }}
          submitLabel='Save Changes'
          cancelVariant='ghost'
          bladeOpen={bladeOpen}
          onBladeChange={setBladeOpen}
        />
      )}

      {mode === MODE_CONFIRMING && (
        <ConfirmDeleteBody
          activity={activity}
          occurrenceScope={occurrenceScope}
          onDeleted={handleClose}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}
    </SydModal>
  )
}

MeetingDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activity: PropTypes.object,
  config: PropTypes.object,
  members: PropTypes.array
}

export default MeetingDetailModal
